import { Box, Theme, useTheme } from '@mui/material';
import clsx from 'clsx';
import React, { PropsWithChildren } from 'react';

import { BreakPoints } from '../../theme/types';

import { headingContext } from './HeadingContext';

export enum HeadingSize {
  XL = 1,
  LG = 2,
  MD = 3,
  SM = 4,
  XS = 5,
  XXS = 6,
}

export enum HeadingVariant {
  Alert = 'alert',
  Default = 'default',
  Error = 'error',
}

export const headingCID = 'CID-Heading';

const sizeStyles = (theme: Theme, size?: HeadingSize) => {
  switch (size) {
    case HeadingSize.XL:
      return {
        fontSize: theme.typography.fontSizes.xxLarge,
        fontWeight: 'bold',
        [theme.breakpoints.down(BreakPoints.Medium)]: {
          fontSize: '1.5rem',
        },
        [theme.breakpoints.down(BreakPoints.Small)]: {
          fontSize: '1.3rem',
        },
      };
    case HeadingSize.LG:
      return {
        fontSize: theme.typography.fontSizes.xLarge,
        fontWeight: 'bold',
      };
    case HeadingSize.MD:
      return {
        fontSize: theme.typography.fontSizes.large,
        fontWeight: 'bold',
      };
    case HeadingSize.SM:
      return {
        fontSize: theme.typography.fontSizes.medium,
        fontWeight: 'bold',
        lineHeight: '1.1em',
      };
    case HeadingSize.XS:
      return {
        fontSize: theme.typography.fontSizes.small,
      };
    case HeadingSize.XXS:
      return {
        fontSize: theme.typography.fontSizes.small,
      };
    default:
      return {};
  }
};

export interface HeadingProps {
  className?: string;
  size?: HeadingSize;
  type?: string;
  variant?: HeadingVariant;
}

const Heading: React.FC<PropsWithChildren<HeadingProps>> = ({ children, className, size, type, variant = '' }) => {
  const theme = useTheme();
  let level = React.useContext(headingContext);

  if (!level) {
    if (!__PRODUCTION__) {
      throw new Error(`Wrong heading level: ${level}`);
    } else {
      level = 1;
    }
  }

  const Element = `h${level}` as React.ElementType;

  const headingClass = clsx({
    [headingCID]: true,
    [className ?? '']: true,
  });

  return (
    <Box
      className={headingClass}
      component={Element}
      role="heading"
      sx={{
        '& b': {
          color: theme.palette.grey.dark,
        },
        '.MuiDialog-paper > div > &': {
          color: theme.palette.common.white,
        },
        color:
          variant === HeadingVariant.Alert
            ? theme.palette.error.dark
            : (theme.palette.heading ?? theme.palette.grey.midDark),
        display: 'inherit',
        lineHeight: 1.3,
        marginBottom: '.6rem',
        gap: variant === HeadingVariant.Alert ? '0.3rem' : undefined,
        alignItems: variant === HeadingVariant.Alert ? 'baseline' : undefined,
        ...sizeStyles(theme, size ?? level),
      }}
      typeof={type}
    >
      {children}
    </Box>
  );
};

export default Heading;
